import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Order } from '@datorama/akita';
import { RemoteDataPage } from '@lycoris-nubila/ngx-lycoris';
import { TranslateService } from '@ngx-translate/core';
import * as jstz from 'jstimezonedetect';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'environments/environment';
import { UserEmailModelOutput } from 'infrastructure/graphql/content/user/model/user-email-model.output';
import { UserModuleAnswerOutput } from 'infrastructure/graphql/content/user/model/user-module-answer.output';
import { UserModuleStatisticOutput } from 'infrastructure/graphql/content/user/model/user-module-statistic.output';
import { UserModuleSuggestionOutput } from 'infrastructure/graphql/content/user/model/user-module-suggestion.output';
import { UserModuleOutput } from 'infrastructure/graphql/content/user/model/user-module.output';
import { UserPhaseStatsOutput } from 'infrastructure/graphql/content/user/model/user-phase-stats.output';
import { CreateUserModuleAnswersDocumentApiMutation } from 'infrastructure/graphql/content/user/mutation/create-user-module-answers-document.mutation';
import { DeleteUserModuleAnswersApiMutation } from 'infrastructure/graphql/content/user/mutation/delete-user-module-answer.mutation';
import { GenerateUserCVApiMutation } from 'infrastructure/graphql/content/user/mutation/generate-user-cv.mutation';
import { GenerateUserModuleAnswersMutation } from 'infrastructure/graphql/content/user/mutation/generate-user-module-answers.mutation';
import { GenerateUserPushModuleAnswersSynthesisApiMutation } from 'infrastructure/graphql/content/user/mutation/generate-user-push-module-answers-synthesis.api-mutation';
import { MarkUserModuleDescriptionAsVisualizedApiMutation } from 'infrastructure/graphql/content/user/mutation/mark-description-as-visualized.mutation';
import { UpdateUserModuleCoachCommentApiMutation } from 'infrastructure/graphql/content/user/mutation/update-user-module-coach-comment.mutation';
import { UpdateUserModuleRatingApiMutation } from 'infrastructure/graphql/content/user/mutation/update-user-module-rating.mutation';
import { FetchUserEmailModelsApiQuery } from 'infrastructure/graphql/content/user/query/fetch-user-email-models.query';
import { FetchUserModuleSuggestionsApiQuery } from 'infrastructure/graphql/content/user/query/fetch-user-module-suggestions.query';
import { FetchModulesStatisticQuery } from 'infrastructure/graphql/content/user/query/fetch-user-modules-statistic.query';
import { FetchUserPhasesStatsApiQuery } from 'infrastructure/graphql/content/user/query/fetch-user-phases-stats.query';
import { MustAnswerFirstCoachingPlanModuleApiQuery } from 'infrastructure/graphql/content/user/query/must-answer-first-coaching-plan-module.api-query';
import { MustAnswerOnBoardingModuleApiQuery } from 'infrastructure/graphql/content/user/query/must-answer-on-boarding-module.api-query';
import { FetchUserPendingMessagesApiQuery } from 'infrastructure/graphql/messaging/message/query/fetch-coach-pending-messages.query';
import { MessagingUserModulePushOutput } from 'infrastructure/graphql/messaging/user/model/messaging-user-module-push.output';
import { MessagingUserSettingOutput } from 'infrastructure/graphql/messaging/user/model/messaging-user-setting.output';
import { UserModulePushOutput } from 'infrastructure/graphql/messaging/user/model/user-module-push.output';
import { UserUsageStatsOutput } from 'infrastructure/graphql/messaging/user/model/user-usage-stats.output';
import { ResendUserModulePushApiMutation } from 'infrastructure/graphql/messaging/user/mutation/resend-user-module-push.mutation';
import { UpdateMessagingUserSettingsApiMutation } from 'infrastructure/graphql/messaging/user/mutation/update-messaging-user-settings.mutation';
import { UpdateUserGrantNewsLetterApiMutation } from 'infrastructure/graphql/messaging/user/mutation/update-user-grant-newsletter.api-mutation';
import { CountUserModulePushesByStatusApiQuery } from 'infrastructure/graphql/messaging/user/query/count-user-module-pushes-by-status.api-query';
import { FetchMessagingUserSettingsApiQuery } from 'infrastructure/graphql/messaging/user/query/fetch-messaging-user-settings.query';
import { FetchUserModulePushesApiQuery } from 'infrastructure/graphql/messaging/user/query/fetch-user-module-pushes.query';
import { FetchUserNewsletterEnabledApiQuery } from 'infrastructure/graphql/messaging/user/query/fetch-user-newsletter-enabled.query';
import { OwnedUserModulePushesApiQuery } from 'infrastructure/graphql/messaging/user/query/owned-user-module-pushes.api-query';
import { OrganizationOutput } from 'organization/model/organization.output';
import { FetchOrganizationByIdsApiQuery } from 'organization/query/fetch-organization-by-ids.query';
import { AssociateCoachWithCoacheeCmd } from 'user/command/associate-coach-with-coachee.cmd';
import { ConnectToUserNetworkCmd } from 'user/command/connect-to-user-network.cmd';
import { CreateUserCmd } from 'user/command/create-user.cmd';
import { GenerateUserCVCmd } from 'user/command/generate-user-cv.cmd';
import { CreateUserModuleAnswersDocumentCmd } from 'user/command/module/answer/create-user-module-answers-document.cmd';
import { GenerateUserModuleAnswersCmd } from 'user/command/module/answer/generate-user-module-answers.cmd';
import { SaveUserAnswersCmd } from 'user/command/module/answer/save-user-answers.cmd';
import { ResendUserModulePushCmd } from 'user/command/module/push/resend-user-module-push.cmd';
import { UpdateUserModuleCoachCommentCmd } from 'user/command/module/update-user-module-coach-comment.cmd';
import { UpdateUserModuleRatingCmd } from 'user/command/module/update-user-module-rating.cmd';
import { UpdateUserModuleCmd } from 'user/command/module/update-user-module.cmd';
import { SaveUserMoodCmd } from 'user/command/mood/save-user-mood.cmd';
import { CreateUserNoteCmd } from 'user/command/note/create-user-note.cmd';
import { ReconnectToUserNetworkCmd } from 'user/command/reconnect-to-user-network.cmd';
import { SaveUserModuleSpentDurationCmd } from 'user/command/save-user-module-spent-duration.cmd';
import { SynchronizeUserNetworkSelectedCalendarsCmd } from 'user/command/synchronize-user-network-selected-calendars.cmd';
import { UpdateCoachCmd } from 'user/command/update-coach.cmd';
import { UpdateCoacheeObserversCmd } from 'user/command/update-coachee-observers.cmd';
import { UpdateUserAvailabilitiesCmd } from 'user/command/update-user-availabilities.cmd';
import { UpdateUserBiographyCmd } from 'user/command/update-user-biography.cmd';
import { UpdateUserSettingsCmd } from 'user/command/update-user-settings.cmd';
import { UpdateUserCmd } from 'user/command/update-user.cmd';
import { CreateUserTaskExportTaskCmd } from 'user/command/userTaskExportTask/create-user-task-export-task.cmd';
import { Auth0ConnectionOutput } from 'user/model/auth0-connection.output';
import { ExternalCalendarInformationOutput } from 'user/model/external-calendar-information.output';
import { IamUserTaskExportTaskOutput } from 'user/model/iam-user-task-export-task.output';
import { StatisticsUserOutput } from 'user/model/statistics-user.output';
import { UserAvailableSlotOutput } from 'user/model/user-available-slot.output';
import { UserMfaEngageOutput } from 'user/model/user-mfa-engage.output';
import { UserMoodOutput } from 'user/model/user-mood.output';
import { UserNetworkState } from 'user/model/user-network.state';
import { UserNetworkType } from 'user/model/user-network.type';
import { UserNoteOutput } from 'user/model/user-note.output';
import { UserNoteSort } from 'user/model/user-note.sort';
import { UserReferenceInput } from 'user/model/user-reference.input';
import { UserReferenceOutput } from 'user/model/user-reference.output';
import { UserStatisticsOutput } from 'user/model/user-statistics.output';
import { UserTaskExportTaskStatus } from 'user/model/user-task-export-task-status';
import { UserTaskExportTaskSort } from 'user/model/user-task-export-task.sort';
import { UserOutput } from 'user/model/user.output';
import { UserSort } from 'user/model/user.sort';
import { AssociateCoachWithCoacheeApiMutation } from 'user/mutation/associate-coach-with-coachee.mutations';
import { AssociateUserWithCoachApiMutation } from 'user/mutation/associate-user-with-coach.mutation';
import { ConnectToUserNetworkApiMutation } from 'user/mutation/connect-to-user-network.mutations';
import { CreateUserMoodApiMutation } from 'user/mutation/create-user-mood.mutation';
import { CreateUserNoteApiMutation } from 'user/mutation/create-user-note.mutation';
import { CreateUserTaskExportTaskApiMutation } from 'user/mutation/create-user-task-export-task.api-mutation';
import {
	CreateUserApiMutation,
	CreateUserParams,
} from 'user/mutation/create-user.mutation';
import { DeleteUserNoteApiMutation } from 'user/mutation/delete-user-note.mutation';
import { DisableUserMfaApiMutation } from 'user/mutation/disable-user-mfa.mutation';
import { DisassociateCoachWithCoacheeApiMutation } from 'user/mutation/disassociate-coach-with-coachee.mutation';
import { DisconnectFromUserNetworkApiMutation } from 'user/mutation/disconnect-from-user-network.mutations';
import { GenerateUserMfaApiMutation } from 'user/mutation/generate-user-mfa.mutation';
import { MarkMoodAskedApiMutation } from 'user/mutation/mark-mood-asked.api-mutation';
import { MarkMoodDescriptionShownApiMutation } from 'user/mutation/mark-mood-description-shown.api-mutation';
import { ReconnectToUserNetworkApiMutation } from 'user/mutation/reconnect-to-user-network.mutations';
import { SaveUserModuleSpentDurationApiMutation } from 'user/mutation/save-user-module-spent-duration.mutation';
import { SynchronizeUserNetworkSelectedCalendarsApiMutation } from 'user/mutation/synchronize-user-network-selected-calendars.mutations';
import { UpdateCoachAvailabilitiesApiMutation } from 'user/mutation/update-coach-availabilities.mutation';
import { UpdateCoachBiographyApiMutation } from 'user/mutation/update-coach-biography.mutation';
import { UpdateCoachProfileApiMutation } from 'user/mutation/update-coach-profile.mutation';
import { UpdateCoacheeObserversApiMutation } from 'user/mutation/update-coachee-observers.mutation';
import { UpdateUserAvatarApiMutation } from 'user/mutation/update-user-avatar.mutation';
import { UpdateUserLanguageApiMutation } from 'user/mutation/update-user-language.mutation';
import { UpdateUserProfileApiMutation } from 'user/mutation/update-user-profile.mutation';
import { UpdateUserQuoteApiMutation } from 'user/mutation/update-user-quote.mutation';
import { ValidateUserMfaApiMutation } from 'user/mutation/validate-user-mfa.mutation';
import { UserApiPort } from 'user/port/user-api.port';
import { UserSessionPort } from 'user/port/user-session.port';
import { CanExportUserMeetingsApiQuery } from 'user/query/can-export-user-meetings.api-query';
import { FetchAuthenticatedUserApiQuery } from 'user/query/fetch-authenticated-user.query';
import { FetchCoachAvailableSlotsApiQuery } from 'user/query/fetch-coach-availability-slots.query';
import { FetchCoachCoacheesApiQuery } from 'user/query/fetch-coach-coaches.query';
import { FetchCoacheesWithoutCoachApiQuery } from 'user/query/fetch-coachees-without-coach.query';
import { FetchOwnedUserModulePushesQuery } from 'user/query/fetch-owned-user-module-pushes.query';
import { FetchStatisticsUserApiQuery } from 'user/query/fetch-statistics-user.api-query';
import { FetchUserAvailableSlotsQuery } from 'user/query/fetch-user-available-slots.query';
import { FetchUserByIdApiQuery } from 'user/query/fetch-user-by-id.query';
import { FetchUserConnectedDaysCountApiQuery } from 'user/query/fetch-user-connected-days-count-api-query.service';
import { FetchUserDaysUntilNextLevelUpCountApiQuery } from 'user/query/fetch-user-days-until-levelup-count-api-query.service';
import { FetchLastMoodApiQuery } from 'user/query/fetch-user-mood-by-criteria.query';
import { FetchUserNetworkInfoApiQuery } from 'user/query/fetch-user-network-info.query';
import { FetchUsersByIdsApiQuery } from 'user/query/fetch-users-by-ids.query';
import { ObserveUserAnswersByModulesIdQuery } from 'user/query/module/answer/observe-user-answers-by-modules-id.query';
import { ObserveUserAnswersByQuestionsIdQuery } from 'user/query/module/answer/observe-user-answers-by-questions-id.query';
import { FetchUserModulePushesQuery } from 'user/query/module/push/fetch-user-module-pushes.query';
import { MustAskMoodApiQuery } from 'user/query/must-ask-mood.api-query';
import { ObserveSearchedUserCoachNotesQuery } from 'user/query/note/observe-searched-user-coach-notes.query';
import { ObserveSearchedCoachesIncludingPartnersQuery } from 'user/query/observe-searched-coaches-including-partners.query';
import { OwnedUserMoodsApiQuery } from 'user/query/owned-user-moods.api-query';
import { OwnedUserNotesApiQuery } from 'user/query/owned-user-notes.api-query';
import { SearchCoacheesIncludingPartnersApiQuery } from 'user/query/search-coachees-including-partners.query';
import { SearchCoachesincludingPartnersApiQuery } from 'user/query/search-coaches-including-partners.query';
import { SearchUserCoachNotesApiQuery } from 'user/query/search-user-coach-notes.query';
import { SearchUserTaskExportTasksApiQuery } from 'user/query/search-user-task-export-tasks.api-query';
import { CanExportUserMeetingsQuery } from 'user/query/task-export-task/can-export-user-meetings.query';
import { SearchUserTaskExportTasksQuery } from 'user/query/task-export-task/search-user-task-export-tasks.query';
import { UserExternalCalendarApiQuery } from 'user/query/user-external-calendar.query';
import { UserNetworkStateApiQuery } from 'user/query/user-network-state.query';

import { ApiAdapter, Direction } from '../../common/api.adpater';
import { AddModuleToUserPlanApiMutation } from '../../graphql/content/user/mutation/add-module-to-user-plan.mutation';
import { DeleteModuleFromUserPlanApiMutation } from '../../graphql/content/user/mutation/delete-module-from-user-plan.mutation';
import {
	SaveUserModuleAnswersApiMutation,
	UserModuleAnswersAnswerInput,
} from '../../graphql/content/user/mutation/save-user-module-answers.mutation';
import { UpdateUserModuleApiMutation } from '../../graphql/content/user/mutation/update-user-module.mutation';
import { FetchUserModulesAnswersApiQuery } from '../../graphql/content/user/query/fetch-user-module-answers-by-module-id.query';
import { FetchUserModulesByUserIdApiQuery } from '../../graphql/content/user/query/fetch-user-module-by-user-id.query';
import { FetchUserStatisticsApiQuery as FetchUserIamStatisticsApiQuery } from '../../graphql/iam/user/query/fetch-user-statistics.query';
import { FetchUserUsageStatsApiQuery as FetchUserMessageStatisticsApiQuery } from '../../graphql/messaging/user/query/fetch-user-usage-stats.query';

@Injectable()
export class UserApiAdapter extends ApiAdapter implements UserApiPort {
	constructor(
		userSessionPort: UserSessionPort,
		translateService: TranslateService,
		private _httpClient: HttpClient,
		private _fetchLastMoodApiQuery: FetchLastMoodApiQuery,
		private _fetchUserByIdApiQuery: FetchUserByIdApiQuery,
		private _createUserApiMutation: CreateUserApiMutation,
		private _ownedUserNotesApiQuery: OwnedUserNotesApiQuery,
		private _fetchUsersByIdsApiQuery: FetchUsersByIdsApiQuery,
		private _markMoodAskedApiMutation: MarkMoodAskedApiMutation,
		private _createUserMoodApiMutation: CreateUserMoodApiMutation,
		private _createUserNoteApiMutation: CreateUserNoteApiMutation,
		private _deleteUserNoteApiMutation: DeleteUserNoteApiMutation,
		private _disableUserMfaApiMutation: DisableUserMfaApiMutation,
		private _updateUserQuoteApiMutation: UpdateUserQuoteApiMutation,
		private _fetchCoachCoacheesApiQuery: FetchCoachCoacheesApiQuery,
		private _fetchModulesStatisticQuery: FetchModulesStatisticQuery,
		private _updateUserModuleApiMutation: UpdateUserModuleApiMutation,
		private _updateUserAvatarApiMutation: UpdateUserAvatarApiMutation,
		private _fetchStatisticsUserApiQuery: FetchStatisticsUserApiQuery,
		private _fetchUserNetworkInfoApiQuery: FetchUserNetworkInfoApiQuery,
		private _updateUserProfileApiMutation: UpdateUserProfileApiMutation,
		private _fetchUserPhasesStatsApiQuery: FetchUserPhasesStatsApiQuery,
		private _fetchUserEmailModelsApiQuery: FetchUserEmailModelsApiQuery,
		private _updateUserLanguageApiMutation: UpdateUserLanguageApiMutation,
		private _updateCoachProfileApiMutation: UpdateCoachProfileApiMutation,
		private _fetchUserModulePushesApiQuery: FetchUserModulePushesApiQuery,
		private _fetchUserSettingsApiQuery: FetchMessagingUserSettingsApiQuery,
		private _fetchOrganizationByIdsApiQuery: FetchOrganizationByIdsApiQuery,
		private _fetchUserIamStatisticsApiQuery: FetchUserIamStatisticsApiQuery,
		private _fetchAuthenticatedUserApiQuery: FetchAuthenticatedUserApiQuery,
		private _addModuleToUserPlanApiMutation: AddModuleToUserPlanApiMutation,
		private _associateCoachWithCoachee: AssociateCoachWithCoacheeApiMutation,
		private _fetchUserModulesAnswersApiQuery: FetchUserModulesAnswersApiQuery,
		private _updateCoachBiographyApiMutation: UpdateCoachBiographyApiMutation,
		private _resendUserModulePushApiMutation: ResendUserModulePushApiMutation,
		private _saveUserModuleAnswersApiMutation: SaveUserModuleAnswersApiMutation,
		private _fetchCoachAvailableSlotsApiQuery: FetchCoachAvailableSlotsApiQuery,
		private _fetchUserModulesByUserIdApiQuery: FetchUserModulesByUserIdApiQuery,
		private _fetchUserPendingMessagesApiQuery: FetchUserPendingMessagesApiQuery,
		private _updateUserModuleRatingApiMutation: UpdateUserModuleRatingApiMutation,
		private _updateCoacheeObserversApiMutation: UpdateCoacheeObserversApiMutation,
		private _associateUserWithCoachApiMutation: AssociateUserWithCoachApiMutation,
		private _fetchCoacheesWithoutCoachApiQuery: FetchCoacheesWithoutCoachApiQuery,
		private _fetchUserMessageStatisticsApiQuery: FetchUserMessageStatisticsApiQuery,
		private _deleteUserModuleAnswersApiMutation: DeleteUserModuleAnswersApiMutation,
		private _fetchUserNewsletterEnabledApiQuery: FetchUserNewsletterEnabledApiQuery,
		private _fetchUserModuleSuggestionsApiQuery: FetchUserModuleSuggestionsApiQuery,
		private _generateUserModuleAnswersApiMutation: GenerateUserModuleAnswersMutation,
		private _deleteModuleFromUserPlanApiMutation: DeleteModuleFromUserPlanApiMutation,
		private _fetchUserConnectedDaysCountApiQuery: FetchUserConnectedDaysCountApiQuery,
		private _updateCoachAvailabilitiesApiMutation: UpdateCoachAvailabilitiesApiMutation,
		private _updateUserGrantNewsLetterApiMutation: UpdateUserGrantNewsLetterApiMutation,
		private _updateMessagingUserSettingsApiMutation: UpdateMessagingUserSettingsApiMutation,
		private _searchCoachesIncludingPartnersApiQuery: SearchCoachesincludingPartnersApiQuery,
		private _updateUserModuleCoachCommentApiMutation: UpdateUserModuleCoachCommentApiMutation,
		private _disassociateCoachWithCoacheeApiMutation: DisassociateCoachWithCoacheeApiMutation,
		private _searchCoacheesIncludingPartnersApiQuery: SearchCoacheesIncludingPartnersApiQuery,
		private _createUserModuleAnswersDocumentApiMutation: CreateUserModuleAnswersDocumentApiMutation,
		private _markMoodDescriptionShownApiMutation: MarkMoodDescriptionShownApiMutation,
		private _generateUserMfaApiMutation: GenerateUserMfaApiMutation,
		private _validateUserMfaApiMutation: ValidateUserMfaApiMutation,
		private _generateUserCVApiMutation: GenerateUserCVApiMutation,
		private _searchUserCoachNotesApiQuery: SearchUserCoachNotesApiQuery,
		private _mustAskMoodApiQuery: MustAskMoodApiQuery,
		private _mustAnswerOnBoardingModuleApiQuery: MustAnswerOnBoardingModuleApiQuery,
		private _mustAnswerFirstCoachingPlanModuleApiQuery: MustAnswerFirstCoachingPlanModuleApiQuery,
		private _saveUserModuleSpentDurationApiMutation: SaveUserModuleSpentDurationApiMutation,
		private _fetchUserDaysUntilNextLevelUpCountApiQuery: FetchUserDaysUntilNextLevelUpCountApiQuery,
		private _ownedUserMoodsApiQuery: OwnedUserMoodsApiQuery,
		private _connectToUserNetworkApiMutation: ConnectToUserNetworkApiMutation,
		private _reconnectToUserNetworkApiMutation: ReconnectToUserNetworkApiMutation,
		private _disconnectFromUserNetworkApiMutation: DisconnectFromUserNetworkApiMutation,
		private _userNetworkStateApiQuery: UserNetworkStateApiQuery,
		private _synchronizeUserNetworkSelectedCalendarsApiMutation: SynchronizeUserNetworkSelectedCalendarsApiMutation,
		private _userExternalCalendarApiQuery: UserExternalCalendarApiQuery,
		private _markUserModuleDescriptionAsVisualizedApiMutation: MarkUserModuleDescriptionAsVisualizedApiMutation,
		private _countUserModulePushesByStatusApiQuery: CountUserModulePushesByStatusApiQuery,
		private _generateUserPushModuleAnswersSynthesisApiMutation: GenerateUserPushModuleAnswersSynthesisApiMutation,
		private _searchUserTaskExportTasksApiQuery: SearchUserTaskExportTasksApiQuery,
		private _createUserTaskExportTaskApiMutation: CreateUserTaskExportTaskApiMutation,
		private _canExportUserMeetingsApiQuery: CanExportUserMeetingsApiQuery,
		private _ownedUserModulePushesApiQuery: OwnedUserModulePushesApiQuery
	) {
		super(userSessionPort, translateService);
	}

	public mustAnswerOnBoardingModule(): Observable<boolean> {
		return this.fetch$(this._mustAnswerOnBoardingModuleApiQuery);
	}

	public mustAnswerFirstCoachingPlanModule(): Observable<boolean> {
		return this.fetch$(this._mustAnswerFirstCoachingPlanModuleApiQuery);
	}

	public mustAskMood(): Observable<boolean> {
		return this.fetch$(this._mustAskMoodApiQuery);
	}

	public markMoodAsked(): Observable<void> {
		return this.mutate$(this._markMoodAskedApiMutation);
	}

	public markMoodDescriptionShown(): Observable<void> {
		return this.mutate$(this._markMoodDescriptionShownApiMutation);
	}

	public fetchUserConnectedDaysCount(): Observable<number> {
		return this.fetch$(this._fetchUserConnectedDaysCountApiQuery);
	}

	public fetchUserDaysUntilNextLevelUpCount(): Observable<number> {
		return this.fetch$(this._fetchUserDaysUntilNextLevelUpCountApiQuery);
	}

	public fetchModulesStatistic(): Observable<UserModuleStatisticOutput[]> {
		return this.fetch$(this._fetchModulesStatisticQuery);
	}

	public fetchStatisticsUser(): Observable<StatisticsUserOutput> {
		return this.fetch$(this._fetchStatisticsUserApiQuery);
	}

	public fetchUserModuleSuggestions(): Observable<UserModuleSuggestionOutput[]> {
		return this.fetch$(this._fetchUserModuleSuggestionsApiQuery);
	}

	public isUserNewsletterEnabled(): Observable<boolean> {
		return this.fetch$(this._fetchUserNewsletterEnabledApiQuery);
	}

	public updateUserGrantNewsLetter(grantNewsLetter: boolean): Observable<void> {
		return this.mutate$(this._updateUserGrantNewsLetterApiMutation, {
			grantNewsLetter: !!grantNewsLetter,
		});
	}

	public validateUserMfa(code: string): Observable<void> {
		return this.mutate$(this._validateUserMfaApiMutation, { code });
	}

	public generateUserMfa(): Observable<UserMfaEngageOutput> {
		return this.mutate$(this._generateUserMfaApiMutation);
	}

	public disableUserMfa(): Observable<void> {
		return this.mutate$(this._disableUserMfaApiMutation);
	}

	public disassociateCoachWithCoachee(coacheeId: string): Observable<string> {
		return this.mutate$(this._disassociateCoachWithCoacheeApiMutation, {
			coacheeId,
		});
	}

	public fetchMessagingUserSettings(
		ownerId: string
	): Observable<MessagingUserSettingOutput[]> {
		return this.fetch$(this._fetchUserSettingsApiQuery, { ownerId });
	}

	public deleteUserNote(noteId: string): Observable<string> {
		return this.mutate$(this._deleteUserNoteApiMutation, { noteId });
	}

	public createUserNote(cmd: CreateUserNoteCmd): Observable<string> {
		return this.mutate$(this._createUserNoteApiMutation, {
			targetId: cmd.targetId,
			note: cmd.note,
		});
	}

	public ownedUserNotes(userId: string): Observable<UserNoteOutput[]> {
		return this.fetch$(this._ownedUserNotesApiQuery, { userId });
	}

	public createUserModuleAnswersDocument(
		cmd: CreateUserModuleAnswersDocumentCmd
	): Promise<boolean> {
		return this.mutate(this._createUserModuleAnswersDocumentApiMutation, {
			input: {
				organizationModuleId: cmd.organizationModuleId,
				splitByRelationship: cmd.splitByRelationship,
				statistics: cmd.statistics,
				userId: cmd.userId,
			},
		});
	}

	public generateUserModuleAnswers(
		cmd: GenerateUserModuleAnswersCmd
	): Promise<string> {
		return this.mutate(this._generateUserModuleAnswersApiMutation, {
			input: {
				organizationModuleId: cmd.organizationModuleId,
				splitByRelationship: cmd.splitByRelationship,
				userId: cmd.userId,
				userModuleAnswersExportType: cmd.userModuleAnswersExportType,
			},
		});
	}

	public updateCoacheeObservers(
		cmd: UpdateCoacheeObserversCmd
	): Observable<UserOutput> {
		return this.mutate$(this._updateCoacheeObserversApiMutation, {
			input: {
				observersId: cmd.observersId,
				coacheeId: cmd.coacheeId,
			},
		});
	}

	public createUser(cmd: CreateUserCmd): Promise<UserOutput> {
		return this.mutate<UserOutput, CreateUserParams>(
			this._createUserApiMutation,
			{
				input: {
					emailAddress: cmd.emailAddress,
					firstName: cmd.firstName,
					lastName: cmd.lastName,
					organizationId: cmd.organizationId,
					phoneNumber: cmd.phoneNumber,
					role: cmd.role,
					welcomeMessage: cmd.welcomeMessage,
					coachId: cmd.coachId,
					language: cmd.language,
				},
			}
		);
	}

	public resendUserModulePush(
		cmd: ResendUserModulePushCmd
	): Promise<UserModulePushOutput[]> {
		return this.mutate(this._resendUserModulePushApiMutation, {
			input: {
				contactId: cmd.contactId,
				pushOrgModuleId: cmd.pushOrgModuleId,
				userId: cmd.userId,
			},
		});
	}

	public addModuleToPlan(userId: string, moduleId: string): Promise<string> {
		return this.mutate(this._addModuleToUserPlanApiMutation, {
			moduleId,
			userId,
		});
	}

	public associateUserWithCoach(coachId: string): Promise<UserOutput> {
		return this.mutate(this._associateUserWithCoachApiMutation, {
			coachId,
		});
	}

	public deleteUserModuleAnswers(
		userId: string,
		moduleId: string
	): Promise<string[]> {
		return this.mutate(this._deleteUserModuleAnswersApiMutation, {
			input: { moduleId, userId },
		});
	}

	public deleteModuleFromPlan(userId: string, moduleId: string): Promise<void> {
		return this.mutate(this._deleteModuleFromUserPlanApiMutation, {
			input: { moduleId, userId },
		});
	}

	public searchCoachesIncludingPartners(
		query: ObserveSearchedCoachesIncludingPartnersQuery
	): Promise<RemoteDataPage<UserOutput>> {
		return this.fetch(this._searchCoachesIncludingPartnersApiQuery, {
			query: {
				name: query.name,
				offset: query.offset,
				chunkSize: query.chunkSize,
				sortDirection: Direction.ASC,
				sortProperty: UserSort.LAST_NAME,
				organizationId: query.organizationId,
			},
		}).then(
			(page) =>
				({
					data: page.elements,
					totalElements: page.totalElements,
				} as RemoteDataPage<UserOutput>)
		);
	}

	public fetchUserAnswersByQuestionsId(
		query: ObserveUserAnswersByQuestionsIdQuery
	): Promise<UserModuleAnswerOutput[]> {
		return this.fetch(this._fetchUserModulesAnswersApiQuery, {
			input: { questionsId: query.questionsId, userId: query.userId },
		});
	}

	public fetchUserAvailableSlots(
		query: FetchUserAvailableSlotsQuery
	): Promise<UserAvailableSlotOutput[]> {
		return this.fetch(this._fetchCoachAvailableSlotsApiQuery, {
			query: {
				coachId: query.userId,
				endDateTime: query.endTime,
				startDateTime: query.startTime,
			},
		});
	}

	public fetchUserModulePushes(
		query: FetchUserModulePushesQuery
	): Observable<UserModulePushOutput[]> {
		return this.fetch$(this._fetchUserModulePushesApiQuery, {
			input: { userId: query.userId, contactId: query.contactId },
		});
	}

	public fetchUserById(id: string): Observable<UserOutput> {
		return this.fetch$(this._fetchUserByIdApiQuery, { id });
	}

	public fetchUsersByIds(ids: string[]): Promise<UserOutput[]> {
		return this.fetch(this._fetchUsersByIdsApiQuery, { ids });
	}

	public fetchOrganizationsByIds(
		ids: string[]
	): Observable<OrganizationOutput[]> {
		return this.fetch$(this._fetchOrganizationByIdsApiQuery, { ids });
	}

	public fetchUserByJwToken(): Observable<UserOutput> {
		return this.fetch$(this._fetchAuthenticatedUserApiQuery);
	}

	public fetchUserModuleAnswersByModulesId(
		query: ObserveUserAnswersByModulesIdQuery
	): Promise<UserModuleAnswerOutput[]> {
		return this.fetch(this._fetchUserModulesAnswersApiQuery, {
			input: {
				reportJobOfferId: query.reportJobOfferId,
				reportTaskId: query.reportTaskId,
				allContacts: query.allContacts,
				orgModulesId: query.modulesId,
				contactId: query.contactId,
				userId: query.userId,
			},
		});
	}

	public fetchUserModulesByUserId(userId: string): Promise<UserModuleOutput[]> {
		return this.fetch(this._fetchUserModulesByUserIdApiQuery, {
			userId,
		});
	}

	public fetchLastMood(): Observable<UserMoodOutput> {
		return this.fetch$(this._fetchLastMoodApiQuery);
	}

	public fetchOwnedMoods(): Observable<UserMoodOutput[]> {
		return this.fetch$(this._ownedUserMoodsApiQuery);
	}

	public fetchUserNetworkInfo(
		auth0UserId: string
	): Promise<Auth0ConnectionOutput> {
		return this.fetch(this._fetchUserNetworkInfoApiQuery, {
			auth0UserId,
		});
	}

	public fetchUserPendingMessages(
		authorsId: string[]
	): Promise<{ [key: string]: number }> {
		return this.fetch(this._fetchUserPendingMessagesApiQuery, { authorsId });
	}

	public fetchUserPhasesStats(userId: string): Promise<UserPhaseStatsOutput[]> {
		return this.fetch(this._fetchUserPhasesStatsApiQuery, {
			userId,
		});
	}

	public fetchUserEmailModels(): Observable<UserEmailModelOutput[]> {
		return this.fetch$(this._fetchUserEmailModelsApiQuery);
	}

	public fetchUserUsageStats(): Promise<UserUsageStatsOutput> {
		return this.fetch(this._fetchUserMessageStatisticsApiQuery);
	}

	public fetchUserStatistics(): Promise<UserStatisticsOutput> {
		return this.fetch(this._fetchUserIamStatisticsApiQuery);
	}

	public fetchCoachCoachees(): Promise<UserOutput[]> {
		return this.fetch(this._fetchCoachCoacheesApiQuery);
	}

	public generateUserAvatarUrl(file: File): Promise<string> {
		const apiUrl = `${environment.iamRestApiBaseUrl}/users/avatar`;

		const formData = new FormData();
		formData.append('upload', file, file.name);

		return this._httpClient
			.post(apiUrl, formData, { responseType: 'text' })
			.toPromise();
	}

	public saveUserAnswers(
		cmd: SaveUserAnswersCmd
	): Promise<UserModuleAnswerOutput[]> {
		return this.mutate(this._saveUserModuleAnswersApiMutation, {
			input: {
				answers: cmd.answers.map(
					(answer) =>
						({
							saveDocumentInput: answer.saveDocumentCmd,
							questionId: answer.questionId,
							articleId: answer.articleId,
							taskInput: answer.task,
							pageId: answer.pageId,
							locale: answer.locale,
							value: answer.value,
							type: answer.type,
						} as UserModuleAnswersAnswerInput)
				),
				reportJobOfferId: cmd.reportJobOfferId,
				reportTaskId: cmd.reportTaskId,
				isAutoSave: cmd.isAutoSave,
				contactId: cmd.contactId,
				moduleId: cmd.moduleId,
				graphs: cmd.graphs,
				userId: cmd.userId,
			},
		});
	}

	public saveUserMood(cmd: SaveUserMoodCmd): Observable<string> {
		return this.mutate$(this._createUserMoodApiMutation, {
			feelings: cmd.feelings,
			frequency: cmd.frequency,
			mood: cmd.mood,
		});
	}

	public updateCoach(cmd: UpdateCoachCmd): Promise<UserOutput> {
		return this.mutate(this._updateCoachProfileApiMutation, {
			input: {
				birthDate: cmd.birthDate
					? moment(cmd.birthDate).format('YYYY-MM-DD')
					: null,
				references: [
					...(cmd.certificates || []).map((c) => this.certificateAsInput(c)),
					...(cmd.diplomas || []).map((d) => this.diplomaAsInput(d)),
				],
				shortDescription: cmd.biography,
				sessionPrice: cmd.sessionPrice,
				phoneNumber: cmd.phoneNumber,
				planPrice: cmd.planPrice,
				firstName: cmd.firstName,
				lastName: cmd.lastName,
				timeZone: cmd.timeZone,
				sectors: cmd.sectors,
				currentAddress: cmd.currentAddress,
				birthAddress: cmd.birthAddress,
				linkedInUrl: cmd.linkedInUrl,
				id: cmd.id,
			},
		});
	}

	public updateCoachBiography(cmd: UpdateUserBiographyCmd): Promise<UserOutput> {
		return this.mutate(this._updateCoachBiographyApiMutation, {
			input: { id: cmd.userId, description: cmd.biography },
		});
	}

	public updateMessagingUserSettings(
		cmd: UpdateUserSettingsCmd
	): Observable<MessagingUserSettingOutput[]> {
		return this.mutate$(this._updateMessagingUserSettingsApiMutation, {
			input: {
				ownerId: cmd.id,
				settings: [
					{
						property: 'USER_CONNECTION_REMINDER_EMAIL_SEND_ENABLED',
						value: String(cmd.notifyInactivity),
					},
					{
						property: 'USER_MODULE_UNFINISHED_EMAIL_SEND_ENABLED',
						value: String(cmd.notifyModuleUnfinished),
					},
					{
						property: 'USER_WEEKLY_SUMMARY_EMAIL_SEND_ENABLED',
						value: String(cmd.notifyWeeklyReport),
					},
				],
			},
		});
	}

	public updateUser(cmd: UpdateUserCmd): Promise<UserOutput> {
		return this.mutate(this._updateUserProfileApiMutation, {
			input: {
				birthDate: cmd.birthDate
					? moment(cmd.birthDate).format('YYYY-MM-DD')
					: null,
				moodFrequency: cmd.moodFrequency,
				currentAddress: cmd.livingAddress,
				birthAddress: cmd.originAddress,
				linkedInUrl: cmd.linkedInUrl,
				phoneNumber: cmd.phoneNumber,
				firstName: cmd.firstName,
				lastName: cmd.lastName,
				timeZone: cmd.timeZone,
				id: cmd.id,
			},
		});
	}

	public updateUserAvailabilities(
		cmd: UpdateUserAvailabilitiesCmd
	): Promise<UserOutput> {
		const timeZone = jstz.determine();

		return this.mutate(this._updateCoachAvailabilitiesApiMutation, {
			input: {
				id: cmd.userId,
				timeZone: timeZone.name(),
				availabilities: cmd.availabilities.map((a) => ({
					dayIndex: a.dayIndex,
					endOffset: a.endOffset,
					startOffset: a.startOffset,
					type: a.type,
					id: a.id,
				})),
			},
		});
	}

	public updateUserAvatar(avatarUrl: string): Promise<UserOutput> {
		return this.mutate(this._updateUserAvatarApiMutation, {
			avatarUrl,
		});
	}

	public updateUserLanguage(language: string): Promise<UserOutput> {
		return this.mutate(this._updateUserLanguageApiMutation, {
			language,
		});
	}

	public updateUserModule(cmd: UpdateUserModuleCmd): Promise<UserModuleOutput> {
		return this.mutate(this._updateUserModuleApiMutation, {
			input: cmd,
		});
	}

	public updateUserModuleCoachComment(
		cmd: UpdateUserModuleCoachCommentCmd
	): Promise<UserModuleOutput> {
		return this.mutate(this._updateUserModuleCoachCommentApiMutation, {
			input: cmd,
		});
	}

	public updateUserModuleRating(
		cmd: UpdateUserModuleRatingCmd
	): Promise<UserModuleOutput> {
		return this.mutate(this._updateUserModuleRatingApiMutation, {
			input: cmd,
		});
	}

	public updateUserQuote(quote: string): Promise<UserOutput> {
		return this.mutate(this._updateUserQuoteApiMutation, {
			quote,
		});
	}

	public associateCoachWithCoachee(
		cmd: AssociateCoachWithCoacheeCmd
	): Observable<UserOutput> {
		return this.mutate$(this._associateCoachWithCoachee, {
			input: {
				coacheeId: cmd.coacheeId,
			},
		});
	}

	public fetchCoacheesWithoutCoach(ids: string[]): Promise<UserOutput[]> {
		return this.fetch(this._fetchCoacheesWithoutCoachApiQuery, {
			ids,
		});
	}

	public generateUserCV(cmd: GenerateUserCVCmd): Observable<string> {
		return this.mutate$(this._generateUserCVApiMutation, {
			isGeneratedWithAI: cmd.isGeneratedWithAI,
			cvTemplateId: cmd.cvTemplateId,
			userId: cmd.userId,
			extension: 'DOCX',
			name: cmd.name,
		});
	}

	public saveUserModuleSpentDuration(
		cmd: SaveUserModuleSpentDurationCmd
	): Observable<void> {
		return this.mutate$(this._saveUserModuleSpentDurationApiMutation, {
			moduleId: cmd.moduleId,
			spentDuration: cmd.spentDuration,
		});
	}

	public searchUserCoachNotes(
		query: ObserveSearchedUserCoachNotesQuery
	): Observable<UserNoteOutput[]> {
		return this.fetch$(this._searchUserCoachNotesApiQuery, {
			note: query.note,
			offset: query.offset,
			chunkSize: query.chunkSize,
			sortDirection: query.sortDirection,
			sortProperty: UserNoteSort.CREATION_DATE_TIME,
			targetId: query.targetId,
			startOfDayTimestamp: query.startOfDayTimestamp,
			endOfDayTimestamp: query.endOfDayTimestamp,
		}).pipe(map((result) => result.elements));
	}

	public reconnectToUserNetwork(
		cmd: ReconnectToUserNetworkCmd
	): Observable<void> {
		return this.mutate$(this._reconnectToUserNetworkApiMutation, {
			networkType: cmd.networkType,
			networkRefreshTokenScope: cmd.networkRefreshTokenScope,
		});
	}

	public connectToUserNetwork(cmd: ConnectToUserNetworkCmd): Observable<void> {
		return this.mutate$(this._connectToUserNetworkApiMutation, {
			auth0UserId: cmd.auth0UserId,
			networkType: cmd.networkType,
			networkRefreshTokenScope: cmd.networkRefreshTokenScope,
		});
	}

	public disconnectFromUserNetwork(
		networkType: UserNetworkType
	): Observable<void> {
		return this.mutate$(this._disconnectFromUserNetworkApiMutation, {
			networkType: networkType,
		});
	}

	public userNetworkState(
		networkType: UserNetworkType
	): Observable<UserNetworkState> {
		return this.fetch$(this._userNetworkStateApiQuery, {
			type: networkType,
		});
	}

	public synchronizeUserNetworkSelectedCalendars(
		cmd: SynchronizeUserNetworkSelectedCalendarsCmd
	): Observable<void> {
		return this.mutate$(
			this._synchronizeUserNetworkSelectedCalendarsApiMutation,
			{
				selectedCalendarsId: cmd.selectedCalendarsId,
				networkType: cmd.networkType,
			}
		);
	}

	public userExternalCalendar(
		type: UserNetworkType
	): Observable<ExternalCalendarInformationOutput[]> {
		return this.fetch$(this._userExternalCalendarApiQuery, {
			type,
		});
	}

	public markUserModuleDescriptionAsVisualized(
		coachingPlanModuleId: string
	): Observable<string> {
		return this.mutate$(this._markUserModuleDescriptionAsVisualizedApiMutation, {
			coachingPlanModuleId: coachingPlanModuleId,
		});
	}

	private certificateAsInput(
		certificate: UserReferenceOutput
	): UserReferenceInput {
		return certificate
			? {
					id: certificate.id,
					startDate: moment(certificate.startDate).format('YYYY-MM-DD'),
					endDate: moment(certificate.endDate).format('YYYY-MM-DD'),
					institution: certificate.institution,
					name: certificate.name,
					type: 'CERTIFICATE',
			  }
			: null;
	}

	private diplomaAsInput(diploma: UserReferenceOutput): UserReferenceInput {
		return diploma
			? {
					id: diploma.id,
					startDate: moment(diploma.startDate).format('YYYY-MM-DD'),
					endDate: moment(diploma.endDate).format('YYYY-MM-DD'),
					institution: diploma.institution,
					name: diploma.name,
					type: 'DIPLOMA',
			  }
			: null;
	}

	public countUserModulePushesByStatusApiQuery(userId: string): Observable<{
		[key: string]: number;
	}> {
		return this.fetch$(this._countUserModulePushesByStatusApiQuery, { userId });
	}

	public generateUserPushModuleAnswersSynthesis(
		coacheeId: string
	): Observable<void> {
		return this.mutate$(this._generateUserPushModuleAnswersSynthesisApiMutation, {
			coacheeId,
		});
	}

	public searchUserTaskExportTask(
		query: SearchUserTaskExportTasksQuery
	): Observable<IamUserTaskExportTaskOutput[]> {
		return this.fetch$(this._searchUserTaskExportTasksApiQuery, {
			sortProperty: query.sort ?? 'CREATION',
			sortDirection: query.direction ?? 'DESC',
			statuses: query.status ? [query.status] : null,
			participantId: query.participantId,
			chunkSize: query.limit || 50,
			offset: query.offset || 0,
			dueEndTimestamp: query.dueEndTimestamp,
			dueStartTimestamp: query.dueStartTimestamp,
			exportedTasksType: query.exportedTasksType,
			exportedTaskStatuses: query.exportedTaskStatuses,
			userId: query.userId,
		}).pipe(map((result) => result.elements));
	}

	public createUserTaskExportTask(
		cmd: CreateUserTaskExportTaskCmd
	): Observable<IamUserTaskExportTaskOutput> {
		return this.mutate$(this._createUserTaskExportTaskApiMutation, {
			userId: cmd.userId,
			exportedTasksType: cmd.exportedTasksType,
			exportedTasksStatuses: cmd.exportedTasksStatuses,
			exportedTasksDueTimestamp: cmd.exportedTasksDueTimestamp,
			exportedTasksParticipantId: cmd.exportedTasksParticipantId,
		});
	}

	public canExportUserMeetings(
		query: CanExportUserMeetingsQuery
	): Observable<UserTaskExportTaskStatus> {
		return this.fetch$(this._canExportUserMeetingsApiQuery, {
			userId: query.userId,
			participantId: query.participantId,
			exportTaskId: query.exportTaskId,
		});
	}

	public ownedUserModulePushes(
		query: FetchOwnedUserModulePushesQuery
	): Observable<MessagingUserModulePushOutput[]> {
		return this.fetch$(this._ownedUserModulePushesApiQuery, {
			organizationModuleId: query.organizationModuleId,
			ownerId: query.ownerId,
		});
	}
}
